import { observer } from "mobx-react-lite";
import React, { useState, useCallback, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ChatHistoryList from "./ChatHistoryList";
import ChatHistoryDetail from "./ChatHistoryDetail";
import ChatFeedbackPanel from "./ChatFeedbackPanel";
import { ChatSession, AcxAgentService } from "../../../../../services/AcxAgentService";

type Props = {
    id: string;
};

const OrganizationChatReview: React.FC<Props> = observer(({ id }) => {
    const { chatId: urlChatId } = useParams<{ chatId?: string }>();
    const navigate = useNavigate();
    const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
    const [selectedChatSession, setSelectedChatSession] = useState<ChatSession | null>(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const selectedChatIdRef = useRef<string | null>(null);
    const loadChatRequestIdRef = useRef(0);

    // Load chat from URL if chatId is present
    useEffect(() => {
        const loadChatById = async (chatId: string) => {
            const requestId = ++loadChatRequestIdRef.current;
            try {
                const agentService = new AcxAgentService();
                // Get all chats and find the one with matching ID
                const startDate = new Date(2024, 0, 1).toISOString();
                const endDate = new Date().toISOString();
                const sessions = await agentService.getAllChats(startDate, endDate, id);
                const session = sessions.find(s => s.id === chatId);

                if (session && requestId === loadChatRequestIdRef.current) {
                    selectedChatIdRef.current = chatId;
                    setSelectedChatId(chatId);
                    setSelectedChatSession(session);
                }
            } catch (error) {
                console.error("Failed to load chat:", error);
            }
        };
        
        if (urlChatId && urlChatId !== selectedChatIdRef.current) {
            loadChatById(urlChatId);
        }
    }, [urlChatId, id]);

    const handleChatSelect = useCallback((chatId: string, chatSession: ChatSession) => {
        loadChatRequestIdRef.current += 1;
        selectedChatIdRef.current = chatId;
        setSelectedChatId(chatId);
        setSelectedChatSession(chatSession);
        setShowFeedback(false);
        // Update URL when chat is selected
        navigate(`/admin/organizations/${id}/chat-review/${chatId}`, { replace: true });
    }, [id, navigate]);

    const handleFeedbackToggle = () => {
        setShowFeedback(!showFeedback);
    };

    return (
        <Box sx={{ display: "flex", height: "100%", width: "100%", overflow: "hidden" }}>
            <Box sx={{ width: 320, borderRight: 1, borderColor: "divider", overflow: "auto" }}>
                <ChatHistoryList 
                    organizationId={id} 
                    selectedChatId={selectedChatId}
                    onChatSelect={handleChatSelect}
                />
            </Box>
            
            <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
                <Box sx={{ flex: showFeedback ? "0 0 60%" : 1, overflow: "auto" }}>
                    <ChatHistoryDetail
                        organizationId={id}
                        chatSession={selectedChatSession}
                        onFeedbackToggle={handleFeedbackToggle}
                        showFeedback={showFeedback}
                    />
                </Box>
                
                {showFeedback && selectedChatId && (
                    <Box sx={{ flex: "0 0 40%", borderLeft: 1, borderColor: "divider", overflow: "auto" }}>
                        <ChatFeedbackPanel 
                            chatId={selectedChatId}
                            onClose={() => setShowFeedback(false)}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
});

export default OrganizationChatReview;
